import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminItemsAPI } from '../api/client';

const PER_PAGE = 66;

const CAT_LABELS = {
  all:      'Всі',
  shoes:    'Взуття',
  clothing: 'Одяг',
};

const ORIENT_LABELS = {
  all:       'Всі',
  landscape: 'Landscape',
  portrait:  'Portrait',
};

function _bustUrl(url) {
  if (!url) return url;
  try {
    const raw = localStorage.getItem('alldrop_photoTs');
    if (!raw) return url;
    const map = JSON.parse(raw);
    const base = url.split('?')[0];
    const ts = map[base];
    return ts ? `${base}?t=${ts}` : url;
  } catch { return url; }
}

export default function PhotoCatalogSection() {
  const [tab, setTab]             = useState('unverified'); // unverified | verified
  const [orientation, setOrient]  = useState('all');
  const [category, setCategory]   = useState('all');
  const [page, setPage]           = useState(1);
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(new Set());
  const [rotating, setRotating]   = useState(false);
  const [rotateResult, setRotateResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [scan, setScan]           = useState({ running: false, done: 0, total: 0, error: null });
  const scanPollRef               = useRef(null);
  const [bustVersion, setBustVersion] = useState(0);

  const load = useCallback(async (pg, orient, cat, verified) => {
    setLoading(true);
    try {
      const r = await adminItemsAPI.getImageCatalog({
        orientation: orient,
        category: cat,
        verified: verified === 'verified' ? 'true' : 'false',
        page: pg,
        per_page: PER_PAGE,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setScan(r.data.scan || { running: false, done: 0, total: 0 });
      if (r.data.scan?.running) startScanPoll();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    load(page, orientation, category, tab);
  }, [page, orientation, category, tab]); // eslint-disable-line

  useEffect(() => {
    const handler = () => setBustVersion(v => v + 1);
    window.addEventListener('photo-rotated', handler);
    return () => window.removeEventListener('photo-rotated', handler);
  }, []);

  const startScanPoll = useCallback(() => {
    if (scanPollRef.current) return;
    scanPollRef.current = setInterval(async () => {
      try {
        const r = await adminItemsAPI.getScanImageSizesStatus();
        setScan(r.data);
        if (!r.data.running) {
          clearInterval(scanPollRef.current);
          scanPollRef.current = null;
          load(1, orientation, category, tab);
        }
      } catch {}
    }, 1500);
  }, [orientation, category, tab, load]);

  useEffect(() => () => { if (scanPollRef.current) clearInterval(scanPollRef.current); }, []);

  const resetFilters = (newTab) => {
    setTab(newTab);
    setPage(1);
    setSelected(new Set());
    setRotateResult(null);
  };

  const handleOrient = (v) => { setOrient(v); setPage(1); setSelected(new Set()); };
  const handleCat    = (v) => { setCategory(v); setPage(1); setSelected(new Set()); };

  const handleStartScan = async () => {
    try {
      await adminItemsAPI.startScanImageSizes();
      setScan(s => ({ ...s, running: true }));
      startScanPoll();
    } catch (e) { alert('Помилка: ' + e.message); }
  };

  const toggleSelect = (url) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  };
  const selectAll   = () => setSelected(new Set(items.map(i => i.url)));
  const deselectAll = () => setSelected(new Set());

  const handleRotateBulk = async (degrees) => {
    if (selected.size === 0) return;
    setRotating(true);
    setRotateResult(null);
    const urls = [...selected];
    try {
      const r = await adminItemsAPI.rotatePhotosBulk(urls, degrees);
      const now = Date.now();
      let map = {};
      try { map = JSON.parse(localStorage.getItem('alldrop_photoTs') || '{}'); } catch {}
      urls.forEach(u => { map[u.split('?')[0]] = now; });
      try { localStorage.setItem('alldrop_photoTs', JSON.stringify(map)); } catch {}
      window.dispatchEvent(new Event('photo-rotated'));
      setRotateResult({ rotated: r.data.rotated, errors: r.data.errors.length });
      setSelected(new Set());
      await load(page, orientation, category, tab);
    } catch (e) {
      setRotateResult({ error: e.message });
    } finally {
      setRotating(false);
    }
  };

  const handleMarkVerified = async (isVerified) => {
    if (selected.size === 0) return;
    setVerifying(true);
    const urls = [...selected];
    try {
      await adminItemsAPI.markPhotosVerified(urls, isVerified);
      setSelected(new Set());
      await load(page, orientation, category, tab);
    } catch (e) {
      alert('Помилка: ' + e.message);
    } finally {
      setVerifying(false);
    }
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const scanPct = scan.total > 0 ? Math.round((scan.done / scan.total) * 100) : 0;

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Каталог зображень</div>

      {/* Scan */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {scan.running ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 160, height: 8, borderRadius: 4, background: 'var(--border,#e5e7eb)' }}>
              <div style={{ width: `${scanPct}%`, height: '100%', borderRadius: 4, background: '#3b82f6', transition: 'width .3s' }} />
            </div>
            <span style={{ fontSize: 12, color: 'var(--lg-text-2)' }}>
              {scan.done.toLocaleString()} / {scan.total.toLocaleString()}…
            </span>
          </div>
        ) : (
          <button onClick={handleStartScan} style={btnStyle('#3b82f6')}>
            {scan.total > 0 ? '↺ Повторно сканувати' : 'Сканувати розміри'}
          </button>
        )}
        {scan.total > 0 && !scan.running && (
          <span style={{ fontSize: 12, color: 'var(--lg-text-3)' }}>
            Відскановано: {scan.total.toLocaleString()} файлів
          </span>
        )}
        {scan.error && <span style={{ fontSize: 12, color: '#ef4444' }}>Помилка: {scan.error}</span>}
      </div>

      {/* Tabs: Не перевірені / Перевірені */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderBottom: '2px solid var(--border,#e5e7eb)' }}>
        {[['unverified', 'Не перевірені'], ['verified', 'Перевірені ✓']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => resetFilters(key)}
            style={{
              padding: '7px 18px', fontSize: 13, cursor: 'pointer', border: 'none',
              background: 'transparent', fontWeight: tab === key ? 700 : 400,
              color: tab === key ? '#3b82f6' : 'var(--lg-text-2)',
              borderBottom: tab === key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: -2,
            }}
          >{label}</button>
        ))}
      </div>

      {/* Фільтри: Категорія + Орієнтація */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginBottom: 4 }}>Розділ</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {Object.entries(CAT_LABELS).map(([v, lbl]) => (
              <button key={v} onClick={() => handleCat(v)} style={filterBtn(category === v)}>{lbl}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginBottom: 4 }}>Орієнтація</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {Object.entries(ORIENT_LABELS).map(([v, lbl]) => (
              <button key={v} onClick={() => handleOrient(v)} style={filterBtn(orientation === v)}>{lbl}</button>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 18, fontSize: 12, color: 'var(--lg-text-3)' }}>
          {total.toLocaleString()} фото
        </div>
      </div>

      {/* Панель дій */}
      {total > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={selectAll} style={btnStyle('#6b7280')}>Вибрати всі ({items.length})</button>
          {tab === 'unverified' && (
            <button
              onClick={async () => {
                const urls = items.map(i => i.url);
                setVerifying(true);
                try {
                  await adminItemsAPI.markPhotosVerified(urls, true);
                  setSelected(new Set());
                  await load(page, orientation, category, tab);
                } catch (e) { alert('Помилка: ' + e.message); }
                finally { setVerifying(false); }
              }}
              disabled={verifying || items.length === 0}
              style={btnStyle('#22c55e', verifying || items.length === 0)}
            >✓ Всі в перевірено ({items.length})</button>
          )}
          {selected.size > 0 && (
            <>
              <button onClick={deselectAll} style={btnStyle('#6b7280')}>Зняти вибір</button>
              <span style={{ fontSize: 13, color: 'var(--lg-text-2)' }}>Вибрано: {selected.size}</span>
              {tab === 'unverified' && (
                <>
                  <button onClick={() => handleRotateBulk(90)}  disabled={rotating} style={btnStyle('#f59e0b', rotating)}>↻ +90°</button>
                  <button onClick={() => handleRotateBulk(-90)} disabled={rotating} style={btnStyle('#f59e0b', rotating)}>↺ −90°</button>
                  <button onClick={() => handleRotateBulk(180)} disabled={rotating} style={btnStyle('#f59e0b', rotating)}>↕ 180°</button>
                  <button onClick={() => handleMarkVerified(true)} disabled={verifying} style={btnStyle('#22c55e', verifying)}>
                    ✓ Перевірено
                  </button>
                </>
              )}
              {tab === 'verified' && (
                <button onClick={() => handleMarkVerified(false)} disabled={verifying} style={btnStyle('#ef4444', verifying)}>
                  ✗ Зняти перевірку
                </button>
              )}
            </>
          )}
          {(rotating || verifying) && (
            <span style={{ fontSize: 12, color: 'var(--lg-text-3)' }}>
              {rotating ? 'Повертаємо…' : 'Зберігаємо…'}
            </span>
          )}
          {rotateResult && !rotating && (
            <span style={{ fontSize: 12, color: rotateResult.error ? '#ef4444' : '#22c55e' }}>
              {rotateResult.error
                ? `Помилка: ${rotateResult.error}`
                : `✓ Повернуто: ${rotateResult.rotated}${rotateResult.errors > 0 ? `, помилок: ${rotateResult.errors}` : ''}`}
            </span>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--lg-text-3)', fontSize: 13 }}>Завантаження…</div>
      ) : total === 0 ? (
        <div style={{ padding: 24, color: 'var(--lg-text-3)', fontSize: 13 }}>
          {scan.total === 0
            ? 'Спочатку натисніть «Сканувати розміри» — одноразова операція ~5 хв.'
            : 'Фото не знайдено.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
          {items.map(item => {
            const displayUrl = _bustUrl(item.url);
            const sel = selected.has(item.url);
            return (
              <div
                key={item.url}
                onClick={() => toggleSelect(item.url)}
                style={{
                  position: 'relative', cursor: 'pointer', borderRadius: 8,
                  border: `2px solid ${sel ? '#3b82f6' : 'var(--border,#e5e7eb)'}`,
                  overflow: 'hidden', background: 'var(--bg-card)',
                }}
              >
                {/* Checkbox */}
                <div style={{
                  position: 'absolute', top: 5, left: 5, zIndex: 2,
                  width: 18, height: 18, borderRadius: 4,
                  background: sel ? '#3b82f6' : 'rgba(0,0,0,0.5)',
                  border: `2px solid ${sel ? '#3b82f6' : 'rgba(255,255,255,0.8)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {sel && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>✓</span>}
                </div>

                {/* Бейдж орієнтації */}
                <div style={{
                  position: 'absolute', top: 5, right: 5, zIndex: 2,
                  background: item.orientation === 'landscape' ? 'rgba(239,68,68,0.85)' : 'rgba(34,197,94,0.85)',
                  color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 3,
                }}>
                  {item.orientation === 'landscape' ? 'L' : 'P'}
                </div>

                {/* Бейдж категорії */}
                <div style={{
                  position: 'absolute', bottom: 22, left: 5, zIndex: 2,
                  background: item.category === 'shoes' ? 'rgba(59,130,246,0.85)' : item.category === 'clothing' ? 'rgba(139,92,246,0.85)' : 'rgba(107,114,128,0.7)',
                  color: '#fff', fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                }}>
                  {item.category === 'shoes' ? 'Взуття' : item.category === 'clothing' ? 'Одяг' : '?'}
                </div>

                {/* Фото */}
                <img
                  src={displayUrl}
                  alt=""
                  key={displayUrl + bustVersion}
                  style={{
                    width: '100%', aspectRatio: '1 / 1', objectFit: 'contain',
                    display: 'block', background: '#f3f4f6',
                    filter: sel ? 'brightness(0.82)' : 'none',
                  }}
                  loading="lazy"
                />

                {/* Розмір */}
                <div style={{ fontSize: 10, color: 'var(--lg-text-3)', textAlign: 'center', padding: '2px 4px', background: 'var(--bg-card)' }}>
                  {item.width}×{item.height}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Пагінація */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))}        disabled={page === 1}          style={btnStyle('#6b7280', page === 1)}>← Попередня</button>
          <span style={{ fontSize: 13, color: 'var(--lg-text-2)', padding: '0 6px' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={btnStyle('#6b7280', page === totalPages)}>Наступна →</button>
        </div>
      )}
    </div>
  );
}

function btnStyle(color, disabled = false) {
  return {
    padding: '5px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer', border: 'none',
    background: disabled ? '#9ca3af' : color, color: '#fff',
    opacity: disabled ? 0.65 : 1,
  };
}

function filterBtn(active) {
  return {
    padding: '4px 12px', borderRadius: 16, fontSize: 12, cursor: 'pointer',
    border: '1px solid', fontWeight: active ? 600 : 400,
    background: active ? '#3b82f6' : 'var(--bg-card)',
    color: active ? '#fff' : 'var(--lg-text)',
    borderColor: active ? '#3b82f6' : 'var(--border,#d1d5db)',
  };
}
